import React from 'react';

import { motion } from 'framer-motion';

import { skeletonVariants } from '@/animations';

interface SkeletonBlockProps {
  className?: string;
}

const SkeletonBlock: React.FC<SkeletonBlockProps> = ({ className = '' }) => (
  <motion.div
    variants={skeletonVariants}
    animate="animate"
    className={`rounded-2xl bg-neutral-200 dark:bg-neutral-800 ${className}`}
  />
);

export const ProductCardSkeleton: React.FC = React.memo(() => (
  <div className="toy-card relative flex h-full flex-col overflow-hidden">
    <div className="relative aspect-[1/1] overflow-hidden rounded-2xl">
      <SkeletonBlock className="h-full w-full rounded-none" />

      <div className="absolute top-4 left-4">
        <SkeletonBlock className="h-6 w-16 rounded-full" />
      </div>

      <div className="absolute top-4 right-4">
        <SkeletonBlock className="h-12 w-12 rounded-2xl" />
      </div>
    </div>

    <div className="flex flex-1 flex-col px-4 py-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <SkeletonBlock className="h-4 w-12" />
          <SkeletonBlock className="h-3 w-8" />
        </div>
        <SkeletonBlock className="h-3 w-16" />
      </div>

      <SkeletonBlock className="mb-2 h-5 w-3/4" />
      <SkeletonBlock className="mb-3 h-5 w-1/2" />

      <div className="flex flex-col gap-1">
        <SkeletonBlock className="h-7 w-24" />
        <SkeletonBlock className="h-4 w-16" />
      </div>

      <div className="mt-auto flex items-center justify-between border-t border-neutral-100/60 pt-3 dark:border-neutral-800/60">
        <SkeletonBlock className="h-3 w-20" />
        <SkeletonBlock className="h-5 w-16 rounded-full" />
      </div>
    </div>
  </div>
));

ProductCardSkeleton.displayName = 'ProductCardSkeleton';

export const ProductRowSkeleton: React.FC = React.memo(() => (
  /* eslint-disable react/no-array-index-key */
  <div className="flex gap-4 rounded-3xl border border-neutral-100/60 bg-white/80 p-3 shadow-sm dark:border-neutral-800 dark:from-neutral-900 dark:to-neutral-950">
    <SkeletonBlock className="h-24 w-24 flex-shrink-0 rounded-2xl" />

    <div className="flex min-w-0 flex-1 flex-col justify-center gap-2">
      <SkeletonBlock className="h-5 w-3/4" />
      <div className="flex gap-1">
        {' '}
        {Array.from({ length: 5 }).map((_, i) => (
          <SkeletonBlock key={`star-${i}`} className="h-3 w-3 rounded-full" />
        ))}
      </div>
      <SkeletonBlock className="h-5 w-20" />
    </div>
  </div>
  /* eslint-enable react/no-array-index-key */
));

ProductRowSkeleton.displayName = 'ProductRowSkeleton';

export const ProductDetailsSkeleton: React.FC = React.memo(() => (
  /* eslint-disable react/no-array-index-key */
  <div className="to-primary-50/10 min-h-screen bg-gradient-to-br from-white via-neutral-50/20 dark:from-neutral-950 dark:via-neutral-950 dark:to-neutral-900/30">
    <div className="from-primary-50/50 to-primary-50 border-primary-100/80 flex items-center border-b bg-gradient-to-r px-6 py-4 dark:border-neutral-800 dark:from-neutral-900/40 dark:to-neutral-950">
      <SkeletonBlock className="h-4 w-64" />
    </div>

    <div className="section-toys">
      <div className="container-toys">
        <div className="grid gap-8 lg:grid-cols-2 lg:items-start">
          <div className="space-y-4">
            <SkeletonBlock className="aspect-[1/1] w-full rounded-3xl" />
            <div className="flex gap-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <SkeletonBlock key={`thumb-${i}`} className="h-20 w-20 rounded-2xl" />
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <SkeletonBlock className="h-6 w-20 rounded-full" />

            <SkeletonBlock className="h-10 w-3/4" />
            <SkeletonBlock className="h-10 w-1/2" />

            <div className="flex items-center gap-4">
              <div className="flex gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <SkeletonBlock key={`rating-star-${i}`} className="h-5 w-5 rounded-full" />
                ))}
              </div>
              <SkeletonBlock className="h-4 w-24" />
            </div>

            <div className="flex items-baseline gap-3">
              <SkeletonBlock className="h-9 w-28" />
              <SkeletonBlock className="h-5 w-20" />
              <SkeletonBlock className="h-5 w-16" />
            </div>

            <div className="space-y-2">
              <SkeletonBlock className="h-4 w-full" />
              <SkeletonBlock className="h-4 w-5/6" />
              <SkeletonBlock className="h-4 w-3/4" />
              <SkeletonBlock className="h-4 w-2/3" />
            </div>

            <div className="space-y-3">
              <SkeletonBlock className="h-4 w-16" />
              <div className="flex gap-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <SkeletonBlock key={`size-${i}`} className="h-12 w-16 rounded-2xl" />
                ))}
              </div>
            </div>

            <div className="flex items-center gap-4">
              <SkeletonBlock className="h-14 w-36 rounded-2xl" />
              <SkeletonBlock className="h-14 flex-1 rounded-2xl" />
            </div>

            <SkeletonBlock className="h-px w-full" />

            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={`meta-${i}`} className="flex items-center gap-3">
                  <SkeletonBlock className="h-5 w-5 rounded-full" />
                  <SkeletonBlock className="h-4 w-48" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="border-t border-neutral-100/10 bg-neutral-50/20 py-12 dark:border-neutral-800/10 dark:bg-neutral-900/40">
      <div className="container-toys space-y-6">
        <div className="flex gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <SkeletonBlock key={`tab-${i}`} className="h-8 w-24 rounded-2xl" />
          ))}
        </div>
        <SkeletonBlock className="h-4 w-full" />
        <SkeletonBlock className="h-4 w-5/6" />
        <SkeletonBlock className="h-4 w-3/4" />
      </div>
    </div>

    <div className="section-toys">
      <div className="container-toys space-y-6">
        <div className="flex items-center gap-3">
          <SkeletonBlock className="h-7 w-48" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <ProductCardSkeleton key={`related-${i}`} />
          ))}
        </div>
      </div>
    </div>
  </div>
  /* eslint-enable react/no-array-index-key */
));

ProductDetailsSkeleton.displayName = 'ProductDetailsSkeleton';

export const ProductGridSkeleton: React.FC<{ count?: number }> = React.memo(({ count = 8 }) => (
  /* eslint-disable react/no-array-index-key */
  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
    {Array.from({ length: count }).map((_, i) => (
      <ProductCardSkeleton key={`grid-${i}`} />
    ))}
  </div>
  /* eslint-enable react/no-array-index-key */
));

ProductGridSkeleton.displayName = 'ProductGridSkeleton';

export const AuthSkeleton: React.FC = React.memo(() => (
  <div className="mx-auto w-full max-w-md space-y-4 px-4 py-8">
    <div className="mb-6 text-center">
      <SkeletonBlock className="mx-auto mb-3 h-8 w-48" />
      <SkeletonBlock className="mx-auto h-4 w-64" />
    </div>

    <div className="space-y-4">
      <div className="space-y-2">
        <SkeletonBlock className="h-3 w-20" />
        <SkeletonBlock className="h-12 w-full rounded-2xl" />
      </div>
      <div className="space-y-2">
        <SkeletonBlock className="h-3 w-16" />
        <SkeletonBlock className="h-12 w-full rounded-2xl" />
      </div>
    </div>

    <SkeletonBlock className="mx-auto h-12 w-48 rounded-2xl" />

    <div className="flex items-center gap-4">
      <SkeletonBlock className="h-px flex-1" />
      <SkeletonBlock className="h-3 w-24" />
      <SkeletonBlock className="h-px flex-1" />
    </div>

    <div className="flex gap-3">
      <SkeletonBlock className="h-12 flex-1 rounded-2xl" />
      <SkeletonBlock className="h-12 flex-1 rounded-2xl" />
    </div>

    <div className="text-center">
      <SkeletonBlock className="mx-auto h-4 w-56" />
    </div>
  </div>
));

AuthSkeleton.displayName = 'AuthSkeleton';

export const CheckoutSkeleton: React.FC = React.memo(() => (
  <div className="flex flex-col gap-8 lg:flex-row">
    <div className="flex-1 space-y-6">
      <SkeletonBlock className="h-6 w-48" />
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <SkeletonBlock className="h-3 w-16" />
          <SkeletonBlock className="h-12 w-full rounded-2xl" />
        </div>
        <div className="space-y-2">
          <SkeletonBlock className="h-3 w-16" />
          <SkeletonBlock className="h-12 w-full rounded-2xl" />
        </div>
      </div>
      <div className="space-y-2">
        <SkeletonBlock className="h-3 w-12" />
        <SkeletonBlock className="h-12 w-full rounded-2xl" />
      </div>
      <div className="space-y-2">
        <SkeletonBlock className="h-3 w-16" />
        <SkeletonBlock className="h-12 w-full rounded-2xl" />
      </div>
      <div className="space-y-2">
        <SkeletonBlock className="h-3 w-16" />
        <SkeletonBlock className="h-12 w-full rounded-2xl" />
      </div>
      <SkeletonBlock className="h-6 w-48" />
      <div className="flex gap-3">
        <SkeletonBlock className="h-16 flex-1 rounded-2xl" />
        <SkeletonBlock className="h-16 flex-1 rounded-2xl" />
      </div>
    </div>

    <aside className="w-full flex-shrink-0 space-y-4 lg:w-[420px]">
      <SkeletonBlock className="h-6 w-36" />
      <div className="space-y-3">
        <SkeletonBlock className="h-4 w-full" />
        <SkeletonBlock className="h-4 w-full" />
        <SkeletonBlock className="h-4 w-3/4" />
      </div>
      <SkeletonBlock className="h-px w-full" />
      <div className="flex items-center justify-between">
        <SkeletonBlock className="h-5 w-16" />
        <SkeletonBlock className="h-7 w-24" />
      </div>
      <SkeletonBlock className="h-14 w-full rounded-2xl" />
    </aside>
  </div>
));

CheckoutSkeleton.displayName = 'CheckoutSkeleton';

export const ContentPageSkeleton: React.FC = React.memo(() => (
  <div className="container-toys space-y-8 py-8">
    <div className="text-center">
      <SkeletonBlock className="mx-auto mb-3 h-10 w-64" />
      <SkeletonBlock className="mx-auto h-5 w-96 max-w-full" />
    </div>

    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      <SkeletonBlock className="h-48 w-full rounded-3xl" />
      <SkeletonBlock className="h-48 w-full rounded-3xl" />
      <SkeletonBlock className="h-48 w-full rounded-3xl" />
    </div>

    <SkeletonBlock className="h-64 w-full rounded-3xl" />

    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      <SkeletonBlock className="h-40 w-full rounded-3xl" />
      <SkeletonBlock className="h-40 w-full rounded-3xl" />
    </div>
  </div>
));

ContentPageSkeleton.displayName = 'ContentPageSkeleton';

export default SkeletonBlock;
